<template>
  <Teleport to="body">
    <div
      ref="rootRef"
      v-if="isOpen"
      class="modal fade show conversation-report bootbox-confirm messages-modal-confirm"
      tabindex="-1"
      role="dialog"
      style="display: block;"
      @click.self="closeModal"
    >
      <div class="modal-dialog report-dialog" role="document">
        <div class="modal-content">
          <button type="button" class="close report-close" aria-label="Close" @click="closeModal"></button>
          <div class="modal-body">
            <div class="report-title">{{ t('messages.report') }}</div>
            <label v-for="reason in reportReasons" :key="reason.value" class="custom-control custom-radio">
              <input
                type="radio"
                class="custom-control-input"
                :value="reason.value"
                v-model="reportReason"
                name="reason"
              />
              <span class="custom-control-label">{{ reason.label }}</span>
            </label>
            <div class="form-group mb-0">
              <textarea
                class="form-control"
                v-model="reportDescription"
                :placeholder="t('messages.description')"
                maxlength="200"
                rows="3"
              ></textarea>
              <div class="report-char-count">{{ reportDescription.length }}/200</div>
            </div>
            <div class="report-auto-block-warning">
              <i class="fa fa-exclamation-triangle"></i>
              <span>{{ t('messages.report_auto_block_warning') }}</span>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-danger"
              @click="submitReport"
              :disabled="!canSubmit"
            >
              <i class="fa fa-spinner fa-spin mr-1" v-if="isSubmitting"></i>
              {{ t('messages.submit_report') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onUnmounted } from 'vue';
import { useI18n } from '../composables/useI18n';

const { t } = useI18n();

interface ReportData {
  userId: string | number;
  reason: string;
  description: string;
}

const props = defineProps<{
  userId: string | number;
}>();

const emit = defineEmits<{
  (e: 'submit', data: ReportData): void;
  (e: 'close'): void;
}>();

const isOpen = ref(false);
const reportReason = ref('');
const reportDescription = ref('');
const isSubmitting = ref(false);
const rootRef = ref<HTMLElement | null>(null);

// Описание обязательно только для причины "Другое" (как на бэкенде — Report.php)
const canSubmit = computed(() =>
  !!reportReason.value &&
  (reportReason.value !== 'other' || !!reportDescription.value.trim()) &&
  !isSubmitting.value
);

// Закрытие по Esc (пока модалка открыта)
const onKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape') closeModal();
};

// Позиционирование как у bootbox-подтверждений: на десктопе — центр колонки
// диалога мессенджера; на мобайле — как у подтверждений (flex + 30vh)
const positionDialog = () => {
  const el = rootRef.value;
  const inner = el && (el.querySelector('.modal-dialog') as HTMLElement | null);
  if (window.innerWidth < 768 || !el || !inner) return;
  const host = document.querySelector('#messages-modal .col-messages-conversation');
  if (!host) return;
  const r = host.getBoundingClientRect();
  inner.style.position = 'fixed';
  inner.style.top = `${Math.round(r.top + r.height / 2)}px`;
  inner.style.left = `${Math.round(r.left + r.width / 2)}px`;
  inner.style.transform = 'translate(-50%, -50%)';
  inner.style.margin = '0';
};

const reportReasons = computed(() => [
  { value: 'spam', label: t('messages.reason_spam') },
  { value: 'profile', label: t('messages.reason_inappropriate') },
  { value: 'rude', label: t('messages.reason_harassment') },
  { value: 'fake', label: t('messages.reason_fake_profile') },
  { value: 'scam', label: t('messages.reason_scam') },
  { value: 'other', label: t('messages.reason_other') },
]);

const openModal = () => {
  isOpen.value = true;
  reportReason.value = '';
  reportDescription.value = '';
  isSubmitting.value = false;
  window.addEventListener('keydown', onKeydown);
  nextTick(() => {
    positionDialog();
    const first = rootRef.value?.querySelector('input[type="radio"]') as HTMLInputElement | null;
    first?.focus();
  });
};

const closeModal = () => {
  isOpen.value = false;
  reportReason.value = '';
  reportDescription.value = '';
  isSubmitting.value = false;
  window.removeEventListener('keydown', onKeydown);
  emit('close');
};

onUnmounted(() => window.removeEventListener('keydown', onKeydown));

const submitReport = async () => {
  if (!reportReason.value) return;
  // Описание обязательно только для причины "Другое" (как на бэкенде — Report.php)
  if (reportReason.value === 'other' && !reportDescription.value.trim()) return;

  isSubmitting.value = true;

  try {
    const csrfMeta = document.querySelector('meta[name="csrf-token"]');
    const csrfToken = csrfMeta ? csrfMeta.getAttribute('content') : '';

    const body = 'reportedUserId=' + encodeURIComponent(String(props.userId)) +
                 '&reason=' + encodeURIComponent(reportReason.value) +
                 '&description=' + encodeURIComponent(reportDescription.value.trim()) +
                 '&_csrf=' + encodeURIComponent(csrfToken || '');

    const response = await fetch('/report/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: body
    });

    const data = await response.json();

    if (data.success) {
      emit('submit', {
        userId: props.userId,
        reason: reportReason.value,
        description: reportDescription.value.trim()
      });
      if ((window as any).messenger?.success) {
        (window as any).messenger.success(data.message);
      }
      closeModal();
    } else {
      const errorMessage = data.message || t('messages.error');
      if ((window as any).messenger?.error) {
        (window as any).messenger.error(errorMessage);
      }
    }
  } catch (error) {
    console.error('Report submission error:', error);
    if ((window as any).messenger?.error) {
      (window as any).messenger.error(t('messages.error'));
    }
  } finally {
    isSubmitting.value = false;
  }
};

defineExpose({
  openModal,
  closeModal
});
</script>
